import React, { useState, useEffect, useRef } from "react";
import styles from "./ExchangesDefaultConfigForm.module.css";
import { useFont } from "../../../contexts/FontContext/FontContext";
import BackButton from "../../BackButton/BackButton";
import ToggleSwitch from "../../ToggleSwitch/ToggleSwitch";
import { getAllChoices, getDefaultPackageWeightAndDimensions } from "../../utils/common";

const ExchangesDefaultConfigForm = ( { showSelectEligibleProducts, setShowSelectEligibleProducts, setShowExchangesDefaultConfigForm } ) => {
	const apiUrl = process.env.REACT_APP_API_URL;
	const token = localStorage.getItem("authToken");
	const choicesBoxRef = useRef(null);
	const [defaultReasonCount, setDefaultReasonCount] = useState(null);
	const [hasFetchedData, setHasFetchedData] = useState(false); //avoid extra update
	const [loading, setLoading] = useState(false);
	const [choices, setChoices] = useState({});
	const [renderDefaultProductDimensionsConfig, setRenderDefaultProductDimensionsConfig] = useState(false);
	const [returnsSwitchState, setReturnsSwitchState] = useState(false);
	const [exchangesSwitchState, setExchangesSwitchState] = useState(false);
	const [selectedReason, setSelectedReason] = useState(null); //pk of selected reason
	const [highlightId, setHighlightId] = useState(null);
	const [renderProductDimensionsConfig, setRenderProductDimensionsConfig] = useState(false);
	const [length, setLength] = useState(0.00);
	const [width, setWidth] = useState(0.00);
	const [height, setHeight] = useState(0.00);
	const [weight, setWeight] = useState(0.00);
	const fontFamilyOne = useFont().scheme_one;
	const maxChoices = 10, maxSubReasons = 4, maxDefaults = 4;
	const reasonIndex = 0, subReasonsIndex = 1, defaultToggleIndex = 2;

	useEffect(() => {
		const init = async () => {
		  	setLoading(true);
		  	setChoices(await getAllChoices(apiUrl, token));	
		  	await getDefaultSwitchStates();
		  	const defaults = await getDefaultPackageWeightAndDimensions(apiUrl, token); // fetch package weight/dimensions
	    	setLength(defaults.length);
	    	setWidth(defaults.width);
	    	setHeight(defaults.height);
	    	setWeight(defaults.weight);		
	    	setLoading(false);
		};
		init();
	}, []);

	//monitor number of defaults set.
	useEffect(() => {
		const numDefaultReasons = Object.values(choices).filter(reasonArr => reasonArr[defaultToggleIndex] === true).length;
		setDefaultReasonCount(numDefaultReasons);
	}, [choices]);

	//retrieve states for default switches
	const getDefaultSwitchStates = async () => {
		try {
			const response = await fetch(`${apiUrl}/api/get_default_feedback_switch_states`, {
				method: "GET",
				headers: {
					"Content-Type": "application/json",
					"Authorization": `Bearer ${token}`,
				},
			});
			const data = await response.json();
			setReturnsSwitchState(data.returns_switch);
			setExchangesSwitchState(data.exchanges_switch);
		} catch (error) { console.error("Error fetching switch states:", error); }
	}

	const updateDefaultProductWeightOrDimensions = async (data) => {
		try {
			const response = await fetch(`${apiUrl}/api/update_default_package_weight_or_dimensions/`, {
				method: "PUT",
				headers: {
					"Content-Type": "application/json",
					"Authorization": `Bearer ${token}`,
				},
				body: JSON.stringify({ ...data })
			});
		} catch (error) { console.error("Error updating default package weight or dimensions:", error); }
	};

	//add a new blank feedback reason to db. Return pk from response as key for choices object.
	const addFeedbackReasonToDatabase = async () => {
		try {
			const response = await fetch(`${apiUrl}/api/add_feedback_reason/`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					"Authorization": `Bearer ${token}`,
				},
			});
			const data = await response.json();
			return data.pk;
		} catch (error) { console.error("Error adding new reason:", error); }
	};

	//add a new blank feedback reason to db. Return pk from response as key for choices object.
	const addSubReasonToDatabase = async (subReasonParentPk) => {
		try {
			const response = await fetch(`${apiUrl}/api/add_sub_reason/`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					"Authorization": `Bearer ${token}`,
				},
				body: JSON.stringify({ subReasonParentPk })
			});
			const data = await response.json();
			return data.pk;
		} catch (error) { console.error("Error adding new sub reason:", error); }
	};

	//add a new top level feedback reason
	const addNewFeedbackReason = async () => {
		if ((!selectedReason && Object.keys(choices).length >= maxChoices) || (selectedReason && Object.keys(choices[selectedReason]?.[subReasonsIndex] || {}).length >= maxSubReasons)) { return; }
		if (selectedReason) { //a top level reason is currently selected
			addNewSubReason();
			return
		}
		const newPk = await addFeedbackReasonToDatabase();
 		const updatedChoices = { ...choices, [newPk]: ["edit new choice", {}, false] };
		setChoices(updatedChoices);
		//Smooth scroll to bottom after DOM update - isnt needed unless there are more than 4 choices, which is the cap for now
		setTimeout(() => {
			if (choicesBoxRef.current) {
				choicesBoxRef.current.scrollTo({
					top: choicesBoxRef.current.scrollHeight,
					behavior: "smooth",
				});
			}
		}, 0); // wait for DOM update
		setHighlightId(String(`reason-${newPk}`));
		setTimeout(() => { setHighlightId(null); }, 1000); // Remove highlight after 1 second
	}

	// Add a new subreason to the currently selected reason
	const addNewSubReason = async () => {
		const newSubPk = await addSubReasonToDatabase(selectedReason);
		setChoices(prevChoices => {
		    const updated = { ...prevChoices };
		    const [reason, subReasons, isDefault] = updated[selectedReason];
		    updated[selectedReason] = [reason, { ...subReasons, [newSubPk]: "edit new sub reason" }, isDefault];
		    return updated;
	  	});
	  	setHighlightId(`sub-${selectedReason}-${newSubPk}`);
	  	setTimeout(() => { setHighlightId(null); }, 1000);
	};

	//update the text of a given feedback choice
	const updateFeedbackReasonText = async (choiceId, updatedReason) => {
		try {
			const response = await fetch(`${apiUrl}/api/update_feedback_reason_text/`, {
				method: "PUT",
				headers: {
					"Content-Type": "application/json",
					"Authorization": `Bearer ${token}`,
				},
				body: JSON.stringify({ choiceId: choiceId, updatedReason: updatedReason })
			});
		} catch (error) { console.error("Error updating reason:", error); }
	}

	//update the text of a given sub choice
	const updateSubReasonText = async (subPk, updatedReason) => {
		try {
			const response = await fetch(`${apiUrl}/api/update_sub_reason_text/`, {
				method: "PUT",
				headers: {
					"Content-Type": "application/json",
					"Authorization": `Bearer ${token}`,
				},
				body: JSON.stringify({ subPk, updatedReason })
			});
		} catch (error) { console.error("Error updating sub reason:", error); }
	}

	//update the default toggle of a given feedback choice
	const updateFeedbackReasonDefaultToggle = async (choiceId) => {
		try {
			const response = await fetch(`${apiUrl}/api/update_feedback_reason_default_toggle/`, {
				method: "PUT",
				headers: {
					"Content-Type": "application/json",
					"Authorization": `Bearer ${token}`,
				},
				body: JSON.stringify({ choiceId: choiceId })
			});
		} catch (error) { console.error("Error updating default toggle:", error); }
	}

	//edit choice text
	const handleChoiceChange = async (choiceId, newValue) => {
		const updatedChoices = {...choices};
		updatedChoices[choiceId][reasonIndex] = newValue;
		await updateFeedbackReasonText(choiceId, newValue);
		setChoices(updatedChoices);
	};

	//edit sub reason text
	const handleSubChoiceChange = async (parentPk, subPk, newValue) => {
		const updatedChoices = {...choices};
		updatedChoices[parentPk][subReasonsIndex][subPk] = newValue;
		await updateSubReasonText(subPk, newValue);
		setChoices(updatedChoices);
	}

	//update state of selected switch in database
	const handleSwitch = async (switch_) => {
		try {
			const response = await fetch(`${apiUrl}/api/toggle_default_feedback_switch/`, {
				method: "PUT",
				headers: {
					"Content-Type": "application/json",
					"Authorization": `Bearer ${token}`,
				},
				body: JSON.stringify({ switch_ })
			});
		} catch (error) { console.error("Error updating default switch:", error); }
	} 

	//delete an ExchangeReason from the db
	const deleteFeedbackReason = async (choiceId) => {
		try {
			const response = await fetch(`${apiUrl}/api/delete_feedback_reason/`, {
				method: "DELETE",
				headers: {
					"Content-Type": "application/json",
					"Authorization": `Bearer ${token}`,
				},
				body: JSON.stringify({ choiceId: choiceId })
			});
		} catch (error) { console.error("Error deleting reason:", error); }
	}

	//delete a sub reason from the db
	const deleteSubReason = async (subPk) => {
		try {
			const response = await fetch(`${apiUrl}/api/delete_sub_reason/`, {
				method: "DELETE",
				headers: {
					"Content-Type": "application/json",
					"Authorization": `Bearer ${token}`,
				},
				body: JSON.stringify({ subPk: subPk })
			});
		} catch (error) { console.error("Error deleting sub reason:", error); }
	}

	//remove choice from ui
	const handleRemoveChoice = async (choiceToRemove) => {
		setChoices((prevChoices) => {
	    	const updatedChoices = { ...prevChoices };
	    	delete updatedChoices[choiceToRemove];
	    	return updatedChoices;
	  	});
	  	await deleteFeedbackReason(choiceToRemove);
	};

	//remove sub reason from ui
	const handleRemoveSubReason = async (subReasonToRemove) => {
		setChoices((prevChoices) => {
	    	const updatedChoices = { ...prevChoices };
	    	delete updatedChoices[selectedReason][subReasonsIndex][subReasonToRemove];
	    	return updatedChoices;
	  	});
	  	await deleteSubReason(subReasonToRemove);
	};

	const toggleDefault = async (choiceId) => {
		await updateFeedbackReasonDefaultToggle(choiceId);
		setChoices((prevChoices) => {
		    const updated = { ...prevChoices };
		    updated[choiceId] = [...updated[choiceId]]; // shallow copy
		    updated[choiceId][defaultToggleIndex] = !updated[choiceId][defaultToggleIndex]; // flip the flag
		    return updated;
	  	});
	};

	return (
		<div className={`${styles.edit_exchange_form_container} ${fontFamilyOne}`}>
			<p className={`titles ${styles.edit_exchange_form_title} fade_in`}>Default configuration</p>
			<div className={styles.edit_exchange_form_box}>
				{loading && (<img className={styles.feedback_choices_loader} src={`${process.env.PUBLIC_URL}/images/shipwheel_loader.gif`} />)}
				{!loading && !renderDefaultProductDimensionsConfig && (
					<>
						{selectedReason && (<p className={`titles ${styles.add_sub_reasons_title} fade_in`}>Add sub reasons for:</p>)}
						<p className={`titles ${styles.add_feedback_title} fade_in`}>{`${selectedReason ? choices[selectedReason][reasonIndex] : "Add feedback selection for customers."}`}</p>
						<div className={`${styles.choices_box} fade_in`} ref={choicesBoxRef}>
							{selectedReason ? (
								Object.keys(choices[selectedReason][subReasonsIndex]).length === 0 ? (<p className={styles.no_sub_reasons}>No sub reasons added.</p>)			
								: (
									Object.entries(choices[selectedReason][subReasonsIndex]).map(([subPk, subValue]) => (
							        	<div key={`sub-${selectedReason}-${subPk}`} className={`${styles.choice_item} ${highlightId === `sub-${selectedReason}-${subPk}` ? styles.highlight_wrapper : ""}`}>	
							        	<p className={`${styles.choice_edit_icon} ${styles.sub}`}>✏️</p>								          		
							          		<div className={`${styles.choice_text_wrapper} ${styles.sub}`}>								        		
								        		<input type="text" className={styles.choice_text} value={subValue} onChange={(e) => handleSubChoiceChange(selectedReason, subPk, e.target.value)} />
								        	</div>		
								        	<img className={`${styles.remove_choice_button} ${styles.sub}`} src={`${process.env.PUBLIC_URL}/images/remove_item.png`} onClick={() => handleRemoveSubReason(subPk)} />
							        	</div>
							      	))
								)
							) : (
								Object.keys(choices).length === 0 ? (<p className={styles.no_options}>No options added.</p>)								
								: (
									Object.entries(choices).map(([choiceId, [reason, subReasons, isDefault]]) => (
										<div key={`reason-${choiceId}`} className={`${styles.choice_item} ${highlightId === `reason-${choiceId}` ? styles.highlight_wrapper : ""} fade_in`}>  							    	  
								        	<div className={styles.choice_text_wrapper}>	
								        		<p className={styles.choice_edit_icon}>✏️</p>
								        		<input type="text" className={styles.choice_text} value={reason} onChange={(e) => handleChoiceChange(choiceId, e.target.value)} />
								        	</div>		
								        	<div className={styles.change_feedback_level_button} onClick={() => setSelectedReason(choiceId)}>See sub reasons</div>
								        	<img className={`${styles.default_lock_button} ${!isDefault && defaultReasonCount === maxDefaults ? styles.dim : ""}`} src={`${process.env.PUBLIC_URL}/${isDefault ? "images/lock_button.png" : "images/unlock_button.png"}`} onClick={() => (isDefault || (!isDefault && defaultReasonCount < maxDefaults)) && toggleDefault(choiceId)} />     					  
								        	<img className={styles.remove_choice_button} src={`${process.env.PUBLIC_URL}/images/remove_item.png`} onClick={() => handleRemoveChoice(choiceId)} />
								        </div>
								    ))
								)
							)}
						</div>
						<div className={`login_buttons fade_in ${styles.add_option_button} ${(!selectedReason && Object.keys(choices).length === maxChoices) || (selectedReason && Object.keys(choices[selectedReason]?.[subReasonsIndex] || {}).length >= maxSubReasons) ? styles.dim : ""}`} onClick={() => addNewFeedbackReason()}>Add reason</div>
						{!selectedReason ? (
							<>
								<div className={`${styles.switch_box} fade_in`}>
									<div className={styles.switch_container}>
										<p className={styles.switch_text}>Show with returns</p>
										<ToggleSwitch isOn={returnsSwitchState} onToggle={() => { setReturnsSwitchState(!returnsSwitchState); handleSwitch("returns"); }} />
									</div>
									<div className={styles.switch_container}>
										<p className={styles.switch_text}>Show with exchanges</p>
										<ToggleSwitch isOn={exchangesSwitchState} onToggle={() => { setExchangesSwitchState(!exchangesSwitchState); handleSwitch("exchanges"); }} />
									</div>
								</div>
								<div className={`login_buttons ${styles.select_products_button} fade_in`} onClick={() => setRenderDefaultProductDimensionsConfig(true)}>Weight and dimensions</div> 
							</>
						) : (<div className={`login_buttons ${styles.view_main_reasons_button}`} onClick={() => setSelectedReason(null)}>View main reasons</div>)}			
					</>
				)}
				{!loading && renderDefaultProductDimensionsConfig && (
					<div className={`fade_in ${styles.weight_and_dimensions_wrapper}`}>
						<p className={styles.weight_and_dimensions_title}>Configure default weight and dimensions.</p>
						<div className={styles.dimensions_input_group}>
					    	<div className={styles.dimension_input}>
					        	<label>Length (in.)</label>
					        	<input type="number" name="length" value={length} onChange={(e) => { setLength(parseFloat(e.target.value)); updateDefaultProductWeightOrDimensions({ length: e.target.value }); }} />
					    	</div>
					    	<div className={styles.dimension_input}>
					        	<label>Width (in.)</label>
					        	<input type="number" name="width" value={width} onChange={(e) => { setWidth(parseFloat(e.target.value)); updateDefaultProductWeightOrDimensions({ width: e.target.value }); }} />
					    	</div>
					      	<div className={styles.dimension_input}>
					        	<label>Height (in.)</label>
					        	<input type="number" name="height" value={height} onChange={(e) => { setHeight(parseFloat(e.target.value)); updateDefaultProductWeightOrDimensions({ height: e.target.value }); }} />
					      	</div>
					      	<div className={styles.dimension_input}>
					        	<label>Weight (oz.)</label>
					        	<input type="number" name="weight" value={weight} onChange={(e) => { setWeight(parseFloat(e.target.value)); updateDefaultProductWeightOrDimensions({ weight: e.target.value }); }} />
					      	</div>
					    </div>
						<div className={`login_buttons ${styles.back_to_feedback_button} fade_in`} onClick={() => setRenderDefaultProductDimensionsConfig(false)}>Feedback form</div> 
					</div>
				)}
			</div>
			<BackButton button_id={styles.edit_exchange_form_back_button} image_id={styles.edit_exchange_form_back_button_arrow} to={"home"} customBack={() => {setShowSelectEligibleProducts(true); setShowExchangesDefaultConfigForm(false); } }/>
		</div>
	);
};

export default ExchangesDefaultConfigForm;